import { FLAGS, } from '@zuruflex/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createContext, createElement, useContext, useMemo } from 'react';
/** Five minutes: a flag is not a live feed, and every app refetches on focus anyway. */
const FLAGS_STALE_MS = 5 * 60_000;
export const flagsQueryKey = (surface, principalKey) => ['flags', surface, principalKey];
const FlagsContext = createContext({ flags: undefined, ready: false, evaluatedAt: null });
/**
 * Fetches the flags this app reads, once, for everything beneath it.
 *
 * Nothing waits on it. Until the answer arrives — and for good if it never
 * does — every `useFlag` returns the catalogue default, which is by
 * definition safe to show. On the phones the query cache is persisted, so
 * a cold start offline reads the last answer instead.
 */
export function FlagsProvider({ client, surface, principalKey, anonymousId, children }) {
    const query = useQuery({
        queryKey: flagsQueryKey(surface, principalKey),
        queryFn: async () => {
            const id = typeof anonymousId === 'function' ? await anonymousId() : anonymousId;
            return client.evaluateFlags(surface, id);
        },
        staleTime: FLAGS_STALE_MS,
        refetchInterval: FLAGS_STALE_MS,
        refetchOnWindowFocus: true,
        // Keep showing the previous person's answer while the next one loads,
        // rather than flashing every flag back to its default on sign-in.
        placeholderData: keepPreviousData,
    });
    const value = useMemo(() => ({
        flags: query.data?.flags,
        ready: query.data !== undefined,
        evaluatedAt: query.data?.evaluatedAt ?? null,
    }), [query.data]);
    return createElement(FlagsContext.Provider, { value }, children);
}
/**
 * Whether a served value has the type its flag declares. An app built
 * against an older catalogue can be sent a flag whose type has since
 * changed; the default is the right answer then, not a crash.
 */
function fits(key, value) {
    switch (FLAGS[key].type) {
        case 'boolean':
            return typeof value === 'boolean';
        case 'number':
            return typeof value === 'number';
        case 'string':
            return typeof value === 'string';
        default:
            return true;
    }
}
/**
 * One flag's value for the signed-in caller, typed from its declaration.
 * The catalogue default until the flags have loaded, and whenever they
 * cannot.
 */
export function useFlag(key) {
    const { flags } = useContext(FlagsContext);
    const served = flags?.[key];
    if (served === undefined || !fits(key, served))
        return FLAGS[key].default;
    return served;
}
/** Everything the provider holds, for a debug panel or a `ready` gate. */
export function useFlags() {
    return useContext(FlagsContext);
}
